import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  Search,
  ThumbsUp,
  Copy,
  RefreshCw,
  Share2,
  Trash2,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "./config";

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [userName, setUserName] = useState("User");
  const [fullName, setFullName] = useState("User");
  const [language, setLanguage] = useState(localStorage.getItem("language") || "en");
  const [chatHistory, setChatHistory] = useState(() => {
    const storedHistory = localStorage.getItem("chatHistory");
    if (storedHistory) {
      const parsed = JSON.parse(storedHistory);
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return parsed.filter((chat) => chat.timestamp > oneWeekAgo);
    }
    return [];
  });

  const [currentChatId, setCurrentChatId] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isGeneratingRef = useRef(false);
  const [likedMessages, setLikedMessages] = useState(new Set());
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedName = localStorage.getItem("name") || "User";
    setFullName(storedName);
    setUserName(storedName.split(" ")[0] || "User");

    const storedLanguage = localStorage.getItem("language") || "en";
    setLanguage(storedLanguage);

    localStorage.setItem("chatHistory", JSON.stringify(chatHistory));

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [chatHistory]);

  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  const handleStopGeneration = () => {
    setIsLoading(false);
    setIsGenerating(false);

    setMessages((prev) => {
      const updated = [...prev];
      const lastMessage = updated[updated.length - 1];

      if (lastMessage && lastMessage.sender === "bot" && !lastMessage.complete) {
        updated[updated.length - 1] = {
          ...lastMessage,
          text:
            lastMessage.text ||
            (language === "en" ? "Response stopped." : "Jibu limesimamishwa."),
          complete: true,
          isThinking: false,
        };
      }

      return updated;
    });
  };


  const simulateTyping = (text, callback) => {
    let index = 0;
    const typingSpeed = 10;
  
    const typeMessage = () => {
      if (!isGeneratingRef.current && index > 0) return;
  
      setMessages((prev) => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
  
        if (lastMessage && lastMessage.sender === "bot" && !lastMessage.complete) {
          updated[updated.length - 1] = {
            ...lastMessage,
            text: text.slice(0, index),
            isThinking: false,
          };
        }
  
        return updated;
      });
  
      if (index < text.length) {
        index += 1;
        setTimeout(typeMessage, typingSpeed);
      } else {
        setMessages((prev) => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
  
          if (lastMessage && lastMessage.sender === "bot" && !lastMessage.complete) {
            updated[updated.length - 1] = {
              ...lastMessage,
              text,
              complete: true,
              isThinking: false,
            };
          }
  
          return updated;
        });
  
        setIsGenerating(false);
        setIsLoading(false);
        if (callback) callback();
      }
    };
  
    typeMessage();
  };



  

const handleSendMessage = async () => {
  if (!input.trim()) return;

  setIsGenerating(true);

  const newMessage = {
    sender: "user",
    text: input,
    id: Date.now(),
  };

  setMessages((prev) => [...prev, newMessage]);

  const chatTitle = input.slice(0, 30) + (input.length > 30 ? "..." : "");
  const newChat = {
    id: Date.now(),
    title: chatTitle,
    messages: [newMessage],
    timestamp: Date.now(),
  };

  setChatHistory((prevHistory) => {
    const updatedHistory = [...prevHistory, newChat];
    localStorage.setItem("chatHistory", JSON.stringify(updatedHistory));
    return updatedHistory;
  });

  setCurrentChatId(newChat.id);

  const userLanguage = localStorage.getItem("language") || "en";
  const currentInput = input;
  setInput("");

  // create bot bubble immediately
  setMessages((prev) => [
    ...prev,
    { sender: "bot", text: "Thinking...", complete: false, id: Date.now(), isThinking: true },
  ]);

  setIsLoading(true);

  try {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.post(
      `${API_BASE_URL}/chat`,
      {
        message: currentInput,
        language: userLanguage,
        history: messages.slice(-6).map((msg) => ({
          sender: msg.sender,
          text: msg.text,
        })),
      },
      { headers }
    );

    const botResponse = response.data.response || "Sorry, I could not generate a response.";

    // small natural pause, then type into same bubble
    setTimeout(() => {
      simulateTyping(botResponse);
    }, 350);
  } catch (error) {
    console.error("Error fetching chat response:", error);

    const placeholderResponseText =
      language === "en"
        ? "Sorry, I couldn’t process your request. Please try again later."
        : "Samahani, siwezi kuchakata ombi lako. Tafadhali jaribu tena baadaye.";

    setTimeout(() => {
      simulateTyping(placeholderResponseText);
    }, 350);
  }
};


const handleRegenerate = async (messageIndex) => {
  const userMessage = messages[messageIndex - 1]?.text;
  if (!userMessage) return;

  setIsGenerating(true);
  setIsLoading(true);

  setMessages((prev) => {
    const updated = [...prev];
    updated[messageIndex] = {
      sender: "bot",
      text: "Thinking...",
      complete: false,
      id: Date.now(),
      isThinking: true,
    };
    return updated;
  });

  try {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.post(
      `${API_BASE_URL}/chat`,
      {
        message: userMessage,
        language,
        history: messages.slice(-6).map((msg) => ({
          sender: msg.sender,
          text: msg.text,
        })),
      },
      { headers }
    );

    const botResponse =
      response.data.response || "Sorry, I could not regenerate that response.";

    setTimeout(() => {
      setMessages((prev) => {
        const updated = [...prev];
        updated[messageIndex] = {
          sender: "bot",
          text: botResponse,
          complete: true,
          id: Date.now(),
          isThinking: false,
        };
        return updated;
      });

      setIsGenerating(false);
      setIsLoading(false);
    }, 350);
  } catch (error) {
    console.error("Error regenerating response:", error);

    const placeholderResponseText =
      language === "en"
        ? "Sorry, I couldn’t regenerate that response right now."
        : "Samahani, siwezi kuzalisha jibu hilo upya kwa sasa.";

    setTimeout(() => {
      setMessages((prev) => {
        const updated = [...prev];
        updated[messageIndex] = {
          sender: "bot",
          text: placeholderResponseText,
          complete: true,
          id: Date.now(),
          isThinking: false,
        };
        return updated;
      });

      setIsGenerating(false);
      setIsLoading(false);
    }, 350);
  }
};





  const handleLike = (messageId) => {
    setLikedMessages((prev) => {
      const newLiked = new Set(prev);
      if (newLiked.has(messageId)) {
        newLiked.delete(messageId);
      } else {
        newLiked.add(messageId);
      }
      return newLiked;
    });
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text.replace(/<br>/g, "\n")).then(() => {
      alert(
        language === "en"
          ? "Copied to clipboard!"
          : "Imenakiliwa kwenye ubao wa kunakili!"
      );
    });
  };

  const handleShare = (text) => {
    if (navigator.share) {
      navigator
        .share({
          title: language === "en" ? "Chat with Umojai" : "Mazungumzo na Umojai",
          text: text.replace(/<br>/g, "\n"),
          url: window.location.href,
        })
        .catch((err) => console.log("Share failed:", err));
    } else {
      const shareUrl = `${window.location.href}?message=${encodeURIComponent(
        text.replace(/<br>/g, "\n")
      )}`;

      navigator.clipboard.writeText(shareUrl).then(() => {
        alert(
          language === "en"
            ? "Share URL copied to clipboard!"
            : "URL ya kushiriki imenakiliwa kwenye ubao!"
        );
      });
    }
  };

  const handleDeleteChat = (chatId, e) => {
    e.stopPropagation();

    const confirmDelete = window.confirm(
      language === "en"
        ? "Are you sure you want to delete this chat?"
        : "Una uhakika unataka kufuta mazungumzo haya?"
    );

    if (!confirmDelete) return;

    setChatHistory((prevHistory) => {
      const newHistory = prevHistory.filter((chat) => chat.id !== chatId);
      localStorage.setItem("chatHistory", JSON.stringify(newHistory));
      return newHistory;
    });

    if (currentChatId === chatId) {
      setMessages([]);
      setCurrentChatId(null);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setInput("");
    setIsGenerating(false);
    setIsLoading(false);
  };

  const handleMinimise = () => {
    setShowHistory((prev) => !prev);
  };

  const handleRevertChat = (chatId) => {
    const selectedChat = chatHistory.find((chat) => chat.id === chatId);
    if (selectedChat) {
      setMessages(selectedChat.messages);
      setCurrentChatId(chatId);
      setIsGenerating(false);
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("name");
    localStorage.removeItem("language");
    navigate("/login");
  };

  const text = {
    en: {
      title: "Hi {userName}, I'm Umojai",
      subtitle: "Adaptive intelligence model for English and Kiswahili.",
      emptyTitle: "How can I help you today?",
      emptySubtitle: "Ask a question, explore an idea, or start a new conversation.",
      prompt1: "What is Umojai and why does it matter for Africa?",
      prompt2: "How can AI improve government service delivery?",
      prompt3: "How can Umojai support education and healthcare in Africa?",
      prompt4: "What are the opportunities of sovereign AI?",
      newChat: "New Chat",
      minimise: "Minimise",
      expand: "Show History",
      searchHistory: "Search history...",
      chatHistory: "Chat History",
      placeholder: "Type here for Umojai...",
      sendButton: "Send",
      stopButton: "Stop",
      logout: "Logout",
      like: "Like",
      copyText: "Copy Text",
      regenerate: "Regenerate",
      share: "Share",
      open: "Open",
      delete: "Delete",
    },
    sw: {
      title: "Habari {userName}, mimi ni Umojai",
      subtitle: "Mfumo wa akili inayobadilika kwa Kiingereza na Kiswahili.",
      emptyTitle: "Ninawezaje kukusaidia leo?",
      emptySubtitle: "Uliza swali, chunguza wazo, au anza mazungumzo mapya.",
      prompt1: "Umojai ni nini na kwa nini ni muhimu kwa Afrika?",
      prompt2: "AI inaweza kuboresha vipi utoaji wa huduma za serikali?",
      prompt3: "Eleza artificial intelligence kwa Kiswahili",
      prompt4: "Ni zipi hatari na fursa za sovereign AI?",    
      newChat: "Mpya",
      minimise: "Punguza",
      expand: "Onyesha Historia",
      searchHistory: "Tafuta historia...",
      chatHistory: "Historia ya Mazungumzo",
      placeholder: "Andika hapa kwa Umojai...",
      sendButton: "Tuma",
      stopButton: "Simamisha",
      logout: "Toka",
      like: "Penda",
      copyText: "Nakili Nakala",
      regenerate: "Zalisha Upya",
      share: "Shiriki",
      open: "Fungua",
      delete: "Futa",
    },
  };

  return (
    <div className="d-flex flex-column vh-100">
      <div
        className="d-flex justify-content-between align-items-center px-4"
        style={{
          height: "64px",
          position: "fixed",
          width: "100%",
          top: 0,
          left: 0,
          zIndex: 1000,
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div className="d-flex align-items-center">
          <img src="/umojai-logo.png" alt="Umojai Logo" style={{ width: "35px" }} />
          <h4 className="fw-bold ms-2" style={{ color: "#2563eb", marginBottom: 0 }}>
            Umojai
          </h4>
        </div>

        <button
          className="btn btn-sm"
          style={{
            background: "linear-gradient(135deg, #2563eb, #4f46e5)",
            color: "#fff",
            borderRadius: "10px",
          }}
          onClick={handleLogout}
        >
          {text[language].logout}
        </button>
      </div>

      {!isMobile && (
        <div
          className="d-flex"
          style={{
            minHeight: "100vh",
            paddingTop: "84px",
            background: "radial-gradient(circle at top, #eef2ff, #ffffff)",
          }}
        >
          {showHistory && (
            <div
              style={{
                width: "280px",
                background: "#fff",
                borderRight: "1px solid #e5e7eb",
                padding: "18px",
              }}
            >
              <div className="d-flex justify-content-between mb-3">
                <button className="btn btn-sm" onClick={handleNewChat}>
                  {text[language].newChat}
                </button>
                <button className="btn btn-sm" onClick={handleMinimise}>
                  {text[language].minimise}
                </button>
              </div>

              <input
                className="form-control mb-3"
                placeholder={text[language].searchHistory}
              />

              <h6 className="fw-bold">{text[language].chatHistory}</h6>

              {chatHistory.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => handleRevertChat(chat.id)}
                  onMouseEnter={() => setHoveredChatId(chat.id)}
                  onMouseLeave={() => setHoveredChatId(null)}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    marginBottom: "8px",
                    background:
                      chat.id === currentChatId
                        ? "linear-gradient(135deg, #2563eb, #4f46e5)"
                        : "#fff",
                    color: chat.id === currentChatId ? "#fff" : "#111",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "200px",
                    }}
                  >
                    {chat.title}
                  </span>

                  {hoveredChatId === chat.id && (
                    <button
                      onClick={(e) => handleDeleteChat(chat.id, e)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: chat.id === currentChatId ? "#fff" : "#6b7280",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex-grow-1 d-flex flex-column p-4">
            <div
              style={{
                maxWidth: "980px",
                margin: "0 auto",
                width: "100%",
                background: "#fff",
                borderRadius: "20px",
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                minHeight: "calc(100vh - 120px)",
              }}
            >
              <h1 className="fw-bold">
                {text[language].title.replace("{userName}", userName)}
              </h1>

              <p className="text-muted mb-4">{text[language].subtitle}</p>

              <div className="flex-grow-1 overflow-auto">
                {messages.length === 0 && !isLoading && (
                  <div style={{ marginTop: "40px" }}>
                    <h4>{text[language].emptyTitle}</h4>
                    <p className="text-muted">{text[language].emptySubtitle}</p>

                    <div className="d-flex gap-2 flex-wrap mt-3">
                      {[text[language].prompt1, text[language].prompt2, text[language].prompt3, text[language].prompt4].map((p, i) => (
                        <button key={i} className="btn btn-light" onClick={() => setInput(p)}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`d-flex ${
                      msg.sender === "user"
                        ? "justify-content-end"
                        : "justify-content-start"
                    } my-2`}
                  >
                    <div
                      className="d-flex flex-column"
                      style={{ alignItems: msg.sender === "user" ? "flex-end" : "flex-start", maxWidth: "100%" }}
                    >
                      <div
                        style={{
                          padding: "10px 14px",
                          borderRadius: "12px",
                          maxWidth: msg.sender === "user" ? "320px" : "70%",
                          minWidth: msg.sender === "user" ? "180px" : "120px",
                          background: msg.sender === "user" ? "#2563eb" : "#f3f4f6",
                          color: msg.sender === "user" ? "#fff" : "#111",
                          whiteSpace: "pre-wrap",
                          wordBreak: "normal",
                          overflowWrap: "break-word",
                        }}
                      >
   {msg.isThinking ? (
  <span className="thinking-inline">
    <span className="thinking-label">Thinking</span>
    <span className="thinking-dots">
      <span>.</span><span>.</span><span>.</span>
    </span>
  </span>
) : (
  msg.text
)}
                      </div>

                      {msg.sender === "bot" && msg.complete && (
                        <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
                          <button
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => handleLike(msg.id)}
                            title={text[language].like}
                          >
                            <ThumbsUp size={15} fill={likedMessages.has(msg.id) ? "#111827" : "none"} />
                          </button>

                          <button
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => handleCopy(msg.text)}
                            title={text[language].copyText}
                          >
                            <Copy size={15} />
                          </button>

                          <button
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => handleRegenerate(index)}
                            title={text[language].regenerate}
                          >
                            <RefreshCw size={15} />
                          </button>

                          <button
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => handleShare(msg.text)}
                            title={text[language].share}
                          >
                            <Share2 size={15} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}


              </div>

              <div className="mt-3 d-flex gap-2">
                <input
                  className="form-control"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={text[language].placeholder}
                />

                <button
                  className="btn"
                  onClick={isGenerating ? handleStopGeneration : handleSendMessage}
                  style={{
                    minWidth: "100px",
                    background: isGenerating
                      ? "#ef4444"
                      : "linear-gradient(135deg, #2563eb, #4f46e5)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "10px 16px",
                    fontWeight: 600,
                  }}
                >
                  {isGenerating ? text[language].stopButton : text[language].sendButton}
                </button>
              </div>
            </div>
          </div>

          {!showHistory && (
            <button
              className="btn"
              onClick={handleMinimise}
              style={{
                position: "fixed",
                top: "84px",
                left: "16px",
                zIndex: 1001,
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "#ffffff",
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "10px 14px",
                fontWeight: 600,
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              }}
            >
              <ChevronDown size={16} />
              {text[language].expand}
            </button>
          )}
        </div>
      )}

      {isMobile && (
                <div
                className="d-flex flex-column"
                style={{
                  minHeight: "100vh",
                  paddingTop: "84px",
                  background: "radial-gradient(circle at top, #eef2ff, #ffffff)",
                }}
              >
                <div className="p-3">
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
                      border: "1px solid #eef2f7",
                    }}
                  >
                    <h2
                      className="fw-bold text-center"
                      style={{ fontSize: "1.5rem", marginBottom: "8px" }}
                    >
                      {text[language].title.replace("{userName}", userName)}
                    </h2>
      
                    <p
                      className="text-muted text-center"
                      style={{ fontSize: "0.95rem", marginBottom: "18px" }}
                    >
                      {text[language].subtitle}
                    </p>
      
                    {showHistory && (
                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                          borderRadius: "14px",
                          padding: "12px",
                          marginBottom: "14px",
                        }}
                      >
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <button
                            className="btn btn-sm"
                            onClick={handleNewChat}
                            style={{
                              background: "linear-gradient(135deg, #2563eb, #4f46e5)",
                              color: "#fff",
                              border: "none",
                              borderRadius: "10px",
                              padding: "8px 12px",
                              fontWeight: 600,
                            }}
                          >
                            {text[language].newChat}
                          </button>
      
                          <button
                            className="btn btn-sm"
                            onClick={handleMinimise}
                            style={{
                              background: "#fff",
                              color: "#111827",
                              border: "1px solid #e5e7eb",
                              borderRadius: "10px",
                              padding: "8px 12px",
                              fontWeight: 600,
                            }}
                          >
                            {showHistory ? text[language].minimise : text[language].expand}
                          </button>
                        </div>
      
                        <h6 className="fw-bold mb-2">{text[language].chatHistory}</h6>
      
                        <div style={{ maxHeight: "140px", overflowY: "auto" }}>
                          {chatHistory.map((chat) => (
                            <div
                              key={chat.id}
                              onClick={() => handleRevertChat(chat.id)}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "10px 12px",
                                borderRadius: "12px",
                                marginBottom: "8px",
                                background:
                                  chat.id === currentChatId
                                    ? "linear-gradient(135deg, #2563eb, #4f46e5)"
                                    : "#ffffff",
                                color: chat.id === currentChatId ? "#ffffff" : "#111827",
                                border: chat.id === currentChatId ? "none" : "1px solid #e5e7eb",
                                cursor: "pointer",
                                fontSize: "0.9rem",
                              }}
                            >
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "180px",
                                }}
                              >
                                {chat.title}
                              </span>
      
                              <button
                                onClick={(e) => handleDeleteChat(chat.id, e)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: chat.id === currentChatId ? "#fff" : "#6b7280",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
      
                    <div
                      style={{
                        minHeight: "320px",
                        maxHeight: "420px",
                        overflowY: "auto",
                        paddingRight: "2px",
                      }}
                    >
                      {messages.length === 0 && !isLoading && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            minHeight: "220px",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              background: "#eef2ff",
                              color: "#3730a3",
                              padding: "6px 10px",
                              borderRadius: "999px",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                          >
                            <Sparkles size={14} />
                            Umojai
                          </div>
      
                          <div>
                            <h5 className="fw-bold" style={{ marginBottom: "6px" }}>
                              {text[language].emptyTitle}
                            </h5>
                            <p
                              className="text-muted"
                              style={{ marginBottom: "12px", fontSize: "0.9rem" }}
                            >
                              {text[language].emptySubtitle}
                            </p>
                          </div>
      
                          <div className="d-flex flex-wrap gap-2">
                            {[
                              text[language].prompt1,
                              text[language].prompt2,
                              text[language].prompt3,
                              text[language].prompt4,
                            ].map((prompt, idx) => (
                              <button
                                key={idx}
                                className="btn btn-sm"
                                onClick={() => setInput(prompt)}
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "12px",
                                  padding: "8px 12px",
                                  color: "#111827",
                                  fontSize: "0.82rem",
                                }}
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
      
                      {messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`d-flex ${
                            msg.sender === "user" ? "justify-content-end" : "justify-content-start"
                          } my-2`}
                        >
                          <div
                            className="d-flex flex-column"
                            style={{
                              alignItems: msg.sender === "user" ? "flex-end" : "flex-start",
                              maxWidth: "100%",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 12px",
                                borderRadius: "14px",
                                maxWidth: msg.sender === "user" ? "260px" : "82%",
                                minWidth: msg.sender === "user" ? "150px" : "110px",
                                background:
                                  msg.sender === "user"
                                    ? "linear-gradient(135deg, #2563eb, #4f46e5)"
                                    : "#f8fafc",
                                color: msg.sender === "user" ? "#ffffff" : "#111827",
                                border: msg.sender === "user" ? "none" : "1px solid #e5e7eb",
                                whiteSpace: "pre-wrap",
                                wordBreak: "normal",
                                overflowWrap: "break-word",
                                fontSize: "0.92rem",
                              }}
                            >
                              {msg.isThinking ? (
  <span className="thinking-inline">
    <span className="thinking-label">Thinking</span>
    <span className="thinking-dots">
      <span>.</span><span>.</span><span>.</span>
    </span>
  </span>
) : (
  msg.text
)}
                            </div>
      
                            {msg.sender === "bot" && msg.complete && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "12px",
                                  marginTop: "6px",
                                }}
                              >
                                <button
                                  className="btn p-0 border-0 bg-transparent"
                                  onClick={() => handleLike(msg.id)}
                                  title={text[language].like}
                                >
                                  <ThumbsUp
                                    size={15}
                                    fill={likedMessages.has(msg.id) ? "#111827" : "none"}
                                  />
                                </button>
      
                                <button
                                  className="btn p-0 border-0 bg-transparent"
                                  onClick={() => handleCopy(msg.text)}
                                  title={text[language].copyText}
                                >
                                  <Copy size={15} />
                                </button>
      
                                <button
                                  className="btn p-0 border-0 bg-transparent"
                                  onClick={() => handleRegenerate(index)}
                                  title={text[language].regenerate}
                                >
                                  <RefreshCw size={15} />
                                </button>
      
                                <button
                                  className="btn p-0 border-0 bg-transparent"
                                  onClick={() => handleShare(msg.text)}
                                  title={text[language].share}
                                >
                                  <Share2 size={15} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
      
                     
                    </div>
      
                    <div
                      style={{
                        marginTop: "16px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "16px",
                        padding: "12px",
                        background: "#fafafa",
                      }}
                    >
                      <textarea
                        className="form-control border-0 bg-transparent"
                        placeholder={text[language].placeholder}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        style={{
                          width: "100%",
                          minHeight: "64px",
                          fontSize: "0.95rem",
                          resize: "none",
                          overflow: "hidden",
                          boxShadow: "none",
                        }}
                      />
      
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            background: "#eef2ff",
                            color: "#3730a3",
                            padding: "6px 10px",
                            borderRadius: "999px",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                          }}
                        >
                          <Sparkles size={13} />
                          Umojai
                        </div>
      
                        <button
                          onClick={isGenerating ? handleStopGeneration : handleSendMessage}
                          className="btn"
                          style={{
                            minWidth: "90px",
                            background: isGenerating
                              ? "#ef4444"
                              : "linear-gradient(135deg, #2563eb, #4f46e5)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "12px",
                            padding: "9px 14px",
                            fontWeight: 600,
                          }}
                        >
                          {isGenerating ? text[language].stopButton : text[language].sendButton}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
      
                {!showHistory && (
                  <button
                    className="btn"
                    onClick={handleMinimise}
                    style={{
                      position: "fixed",
                      top: "84px",
                      left: "16px",
                      zIndex: 1001,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "#ffffff",
                      color: "#111827",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "10px 14px",
                      fontWeight: 600,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                    }}
                  >
                    <ChevronDown size={16} />
                    {text[language].expand}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      };
      
      export default Chatbot;
      
      const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes blinkDots {
    0% { opacity: 0.2; }
    20% { opacity: 1; }
    100% { opacity: 0.2; }
  }

  .thinking-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap; /* 🔥 THIS FIXES YOUR ISSUE */
}

.thinking-label {
  font-size: 0.95rem;
  white-space: nowrap; /* 🔥 prevents "Thin king" */
}

.thinking-dots span {
  display: inline-block;
  font-size: 1.1rem;
  line-height: 1;
  animation: blinkDots 1.4s infinite;
}

  .thinking-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .thinking-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;
document.head.appendChild(styleSheet);
      